import { BCMSCurrency, BCMSDate, BCMSDefaultModel, BCMSRecord, useGlobalController, useParams, useRoute, Workflow } from "@bcm-dev/bcms-vite";
import { default as ConfirmAutopay } from "compid/pages/ConfirmAutoPay";
import PaymentConfirmation from "compid/pages/PaymentConfirmationPage";
import ReviewAndPay from "compid/pages/ReviewAndPayPage";
import { Account, Autopay, Bill, Biller, Payment } from "model/";

// Steps of workflow, key is the route of the step, value is the component
const steps = {
    "/reviewAndPay" : <ReviewAndPay />,
    "/paymentConfirmation" : <PaymentConfirmation />,
    "/autopayConfirmation" : <ConfirmAutopay />
};

/**
 * @function MakeAPaymentWorkflow
 * @returns {React.Component}
 * @description The main component for the multipay workflow
 * @captureState Example of changing state in a capture.
 *  document.dispatchEvent(new CustomEvent("force-workflow-state", {
        detail : {
            value : {
                payment: {
                    paymentAmount: 123,
                    paymentDate: 2,
                    paymentFrom: 1
                }
            }
        }
    }))
 */
export default function MakeAPaymentWorkflow(props) {
    const controller = useGlobalController();
    const { branding } = controller.state;

    // Param from URL provided by route determines which bill we are going to pay
    const {billID, stepID} = useParams();                                      
    const navigate = useRoute();

    const billExists = controller.state.bills.find(item => item.billID === billID) !== undefined;

    // Redirect to "pay bills" if there are no bills in model:
    if (controller.state.bills.length > 0 && billExists) {
        let value = getDefaultPayment(controller, billID);

        // Define the constructor function for forcing state through the 'force-workflow-state' event
        const constructor = record => {
            value = getDefaultPayment(controller, billID);

            // START: Set a fee amount for captures.
            const recordFeeAmount = record.payment ? record.payment.feeAmount : undefined;

            if(recordFeeAmount === undefined) {
                record.payment.feeAmount = Payment.getElectronicFee(branding);
            }
            // END: Set a fee amount for captures.

            const recordPaymentAsObj = { ...value.payment.record, ...record.payment };
            const recordPayment = Payment.init(recordPaymentAsObj);
            const mergedPayment = BCMSDefaultModel.mergeRecords(recordPayment, value.payment);
            const newPayment = Payment.init(mergedPayment);
            const paymentProps = { payment: newPayment };

            const recordBillAsObj = { ...value.bill.record, ...record.bill };
            const recordBill = Bill.init(recordBillAsObj);
            const mergedBill = BCMSDefaultModel.mergeRecords(recordBill, value.bill);
            const newBill = Bill.init(mergedBill);
            const billProps = { bill: newBill };

            // START: Select the fee date and set the "Payment" date to the fee date.
            // Needed for state captures since the fee date (with business offsets)
            // is info not accessible through the state captures.
            const setPaymentDateToFeeDate = record.setPaymentDateToFeeDate;
            if(setPaymentDateToFeeDate !== undefined) {
                const paymentType = value.payment.to.paymentType;
                paymentProps.payment.date = Payment.getFeeDate(paymentType);
            }
            // END: Select the fee date and set the "Payment" date to the fee date.
            // Needed for state captures since the fee date (with business offsets)
            // is info not accessible through the state captures.

            return { ...value, ...paymentProps, ...billProps };
        }

        return (<Workflow value={ value } steps={ Object.values(steps) } step={ stepID } constructor={ constructor } />);
    } else {
        navigate("/PayBills");
    }
}

// Return the steps of the workflow
export const getWorkflowSteps = () => steps;

// getDefaultPayment :: OBJECT -> OBJECT
// Return the default payment for the workflow
export const getDefaultPayment = (controller, billID) => {
    BCMSRecord.globalControllerFor([ Account, Payment, Biller, Bill, Autopay ], controller);

    const bill = billID === undefined 
        ? Bill.firstInstanceOf() 
        : Bill.initByID(billID) ?? Bill.firstInstanceOf();

    const payment = BCMSDefaultModel.payment();
    const autopay = BCMSDefaultModel.autopay();

    autopay.frequency = undefined;
    autopay.duration = undefined;
    autopay.numberOfPayments = "";
    autopay.id = Autopay.nextID();
    autopay.alwaysPay = BCMSCurrency.init(0);
    autopay.finalDeliveryBy = BCMSDate.init().offset(-1);
    autopay.billerID = bill.biller.id;

    payment.id = Payment.nextID();
    payment.to = bill.biller;
    // Only billers with a status of "due" should show a prefilled amount.
    // All other billers will show an amount of "$0.00" by default.
    payment.amount = bill.isDue ? bill.amount.amount ?? "0" : "0";
    // Set the default payment date depending on the payment type
    let paymentDate = payment.date;

    if(payment.to.paymentType === "paper") {
        Array.apply(null, Array(5)).forEach(i => paymentDate.nextBusinessDay());
    } else {
        if(Payment.hasRealTimeGoodFunds("electronic", controller.state.branding)) {
            paymentDate = Payment.getFeeDate("electronic", controller.state.branding);
            paymentDate.nextBusinessDay(); 
        } else {
            paymentDate.nextBusinessDay(); 
        }
         
    }

    payment.date = paymentDate;
    autopay.deliverBy = paymentDate;

    if(payment.to.eBillStatus === "Not Enrolled") { 
        payment.amount = "0";
    }
    
    bill.payment = payment;

    return { payment, bill, autopay, biller: bill.biller };
};